import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message, Card, Row, Col,
  Radio, Tag, Space, Avatar, Typography, DatePicker, Popconfirm, Drawer,
  List, Switch, Popover, QRCode, Tooltip, Badge, Divider
} from 'antd';
import { 
  PlusOutlined, EditOutlined, MessageOutlined,
  UserOutlined, PhoneOutlined, MailOutlined, GlobalOutlined,
  CalendarOutlined, CloseOutlined, DeleteOutlined, ReloadOutlined,
  PhoneFilled, RightOutlined, FileTextOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
// import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import DepartmentSelect from '../components/DepartmentSelect';
import PositionSelect from '../components/PositionSelect';
import AttachmentUpload from '../components/AttachmentUpload';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

// 状态相关函数
const getStatusColor = (status) => {
  const statusColors = {
    'new': 'blue',
    'contacted': 'cyan',
    'qualified': 'green',
    'unqualified': 'red',
    'converted': 'purple',
    'non_employee': 'default',
    'pending': 'orange',
    'interviewed': 'blue',
    'offered': 'green',
    'hired': 'green',
    'rejected': 'red',
    'onboarding': 'purple',
    'active': 'green',
    'inactive': 'red',
    'no_show': 'red',
    'cancelled': 'default',
    'resigning': 'orange'
  };
  return statusColors[status] || 'default';
};

const getStatusText = (status) => {
  const statusTexts = {
    'new': '新线索',
    'contacted': '已联系',
    'qualified': '合格',
    'unqualified': '不合格',
    'converted': '已转化',
    'non_employee': '非员工',
    'pending': '待面试',
    'interviewed': '已面试',
    'offered': '已发offer',
    'hired': '已入职',
    'rejected': '已拒绝',
    'onboarding': '入职中',
    'active': '在职中',
    'inactive': '已离职',
    'no_show': '未到场',
    'cancelled': '已取消',
    'resigning': '离职中'
  };
  return statusTexts[status] || status || '-';
};

// 通用文本渲染函数
const renderTextWithTooltip = (text, maxLines = 3) => {
  if (!text) return '-';
  
  const lines = text.split('\n');
  if (lines.length > maxLines) {
    const truncatedText = lines.slice(0, maxLines).join('\n') + '...';
    return (
      <Tooltip title={text} placement="topLeft">
        <div style={{
          maxHeight: `${(maxLines + 0.5)}em`,
          overflow: 'hidden',
          lineHeight: '1.5em',
          wordBreak: 'break-word',
          whiteSpace: 'pre-line'
        }}>
          {truncatedText}
        </div>
      </Tooltip>
    );
  }
  
  return (
    <div style={{
      lineHeight: '1.5em',
      wordBreak: 'break-word',
      whiteSpace: 'pre-line'
    }}>
      {text}
    </div>
  );
};

const MyLeads = () => {
  const { user, hasPermission } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [editingLead, setEditingLead] = useState(null);
  const [sources, setSources] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [tags, setTags] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'my' 或 'all' - 默认查看所有
  const [filters, setFilters] = useState({
    search: '',
    id: '',
    gender: '',
    age_min: '',
    age_max: '',
    education_level: '',
    work_status: '',
    work_years_min: '',
    work_years_max: '',
    source_id: '',
    rating_id: '',
    status: '',
    tags: [],
    followUpStatus: ''
  });
  const [followUpDrawerVisible, setFollowUpDrawerVisible] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [currentLeadIndex, setCurrentLeadIndex] = useState(-1);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [autoCall, setAutoCall] = useState(false);
  const [showSystemOperations, setShowSystemOperations] = useState(false);
  const [interviewModalVisible, setInterviewModalVisible] = useState(false);
  const [interviewForm] = Form.useForm();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [currentLeadInterviews, setCurrentLeadInterviews] = useState([]);
  const [currentLeadOnboardings, setCurrentLeadOnboardings] = useState([]);
  const uploadRef = useRef(null);
  const [statusEditVisible, setStatusEditVisible] = useState(false);
  const [statusForm] = Form.useForm();
  // 新增：销售归属筛选
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState(''); // 空为不过滤

  // 处理查看模式切换
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const renderFollowUpContent = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/g;
    const tokens = text.split(urlRegex);
    return (
      <div>
        {tokens.map((tok, idx) => {
          if (!tok) return null;
          if (urlRegex.test(tok)) {
            return (
              <span key={idx} style={{ display: 'inline-block', maxWidth: '100%' }}>
                <a href={tok} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{tok}</a>
                <img
                  src={tok}
                  alt="follow-up-img"
                  style={{ width: '30%', maxWidth: 240, borderRadius: 6, marginTop: 6, display: 'block', cursor: 'zoom-in' }}
                  onClick={() => window.open(tok, '_blank')}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </span>
            );
          }
          return <span key={idx}>{tok}</span>;
        })}
      </div>
    );
  };

  const [form] = Form.useForm();
  const [followUpForm] = Form.useForm();
  // const navigate = useNavigate();

  // 调试信息
  console.log('当前用户状态:', user);
  console.log('当前token:', localStorage.getItem('token'));
  console.log('当前筛选器状态:', filters);

  const fetchLeads = useCallback(async (page = 1) => {
    // 如果用户未登录，不发送请求
    if (!user?.id) {
      console.log('用户未登录，跳过获取线索');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
          params.append(key, filters[key]);
        }
      });
      // 根据用户权限和查看模式决定获取哪些线索
      if (hasPermission('lead.view_all')) {
        if (selectedOwnerId) {
          // 选择单个销售：只显示该销售名下线索
          params.append('assigned_to', selectedOwnerId);
        } else if (viewMode === 'my') {
          params.append('assigned_to', user.id);
        } else {
          params.append('assigned_to', 'all_assigned');
        }
      } else {
        params.append('assigned_to', user.id);
      }
      // 添加分页参数
      params.append('page', page);
      params.append('limit', pagination.pageSize);
      
      const response = await api.get(`/leads?${params.toString()}`);
      let leadsData = [];
      
      // 处理新的分页响应格式
      if (response.data && response.data.data) {
        leadsData = response.data.data;
        setPagination(response.data.pagination);
      } else {
        // 兼容旧格式
        leadsData = response.data;
      }
      
      // 前端筛选跟进状态
      if (filters.followUpStatus) {
        console.log('开始筛选预约状态:', filters.followUpStatus, '原始数据量:', leadsData.length);
        leadsData = filterLeadsByFollowUpStatus(leadsData, filters.followUpStatus);
        console.log('筛选后数据量:', leadsData.length);
      }
      
      setLeads(leadsData);
    } catch (error) {
      console.error('获取线索失败:', error);
      message.error(`获取我的线索列表失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [user?.id, pagination.pageSize, filters, viewMode, hasPermission, selectedOwnerId]);

  // 处理分页变化
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    fetchLeads(page);
  };

  // 处理下一条线索
  const handleNextLead = () => {
    if (currentLeadIndex < leads.length - 1) {
      const nextLead = leads[currentLeadIndex + 1];
      handleFollowUp(nextLead);
      
      // 如果开启了自动拨打电话，延迟执行拨打电话
      if (autoCall) {
        setTimeout(() => {
          updateMobilePage(
            nextLead.id,
            nextLead.phone,
            nextLead.name,
            nextLead.last_follow_up_at ? 
              new Date(nextLead.last_follow_up_at).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              }) : ''
          );
        }, 1000); // 延迟1秒执行，确保页面加载完成
      }
    }
  };

  const fetchOptions = useCallback(async () => {
    try {
      const [sourcesRes, ratingsRes, tagsRes] = await Promise.all([
        api.get('/sources'),
        api.get('/ratings'),
        api.get('/tags')
      ]);
      setSources(sourcesRes.data);
      setRatings(ratingsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('获取选项数据失败:', error);
      message.error(`获取选项数据失败: ${error.response?.data?.message || error.message}`);
    }
  }, []);

  // 获取用户列表与每人线索数量
  const fetchOwnerCounts = useCallback(async () => {
    if (!hasPermission('lead.view_all')) return;
    try {
      const resp = await api.get('/leads/assigned-counts');
      const rows = Array.isArray(resp.data) ? resp.data : [];
      setOwnerOptions(rows);
    } catch (e) {
      console.error('获取用户线索数量失败:', e);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchOptions();
    fetchOwnerCounts();
  }, [fetchOptions, fetchOwnerCounts]);

  useEffect(() => {
    if (user?.id) {
      fetchLeads();
    }
  }, [filters, user?.id, viewMode, selectedOwnerId]);

  const handleAdd = () => {
    setEditingLead(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'non_employee' // 默认设置为非员工
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingLead(record);
    form.setFieldsValue({
      ...record,
      tags: Array.isArray(record.tags) ? record.tags.map(tag => tag.id) : []
    });
    setModalVisible(true);
  };



  const handleFollowUp = async (record) => {
    // 先设置基本信息，避免界面闪烁
    setCurrentLead(record);
    // 设置当前线索在列表中的索引
    const index = leads.findIndex(lead => lead.id === record.id);
    setCurrentLeadIndex(index);
    followUpForm.setFieldsValue({
      content: '',
      rating_id: record.rating_id,
      tags: Array.isArray(record.tags) ? record.tags.map(tag => tag.id) : [],
      status: record.status,
      next_contact_at: record.next_contact_at ? dayjs(record.next_contact_at) : dayjs().add(2, 'day')
    });
    setFollowUpDrawerVisible(true);
    
    // 获取当前线索的面试和入职状态信息
    fetchCurrentLeadStatus(record.id);
    
    try {
      // 获取完整的线索详情，确保包含所有字段
      const [leadRes, fuRes, attRes] = await Promise.all([
        api.get(`/leads/${record.id}`),
        api.get(`/leads/${record.id}/follow-ups`),
        api.get(`/upload/lead/${record.id}`)
      ]);
      
      // 使用完整的线索详情更新 currentLead
      const fullLeadData = leadRes.data;
      
      // 检查获取到的数据是否完整
      if (!fullLeadData || !fullLeadData.name || !fullLeadData.phone) {
        // 如果获取的数据不完整，保持使用列表中的数据
        setCurrentLead(record);
      } else {
        setCurrentLead(fullLeadData);
      }
      
      const fuList = fuRes.data || [];
      setFollowUpHistory(fuList);
      const leadAtt = attRes.data?.data || attRes.data || [];
      // 如果附件为空，则从跟进内容中提取图片URL作为补充显示
      let combined = leadAtt;
      try {
        const urlRegex = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/g;
        const imageUrls = new Set();
        fuList.forEach(item => {
          const parts = (item.content || '').split(urlRegex).filter(Boolean);
          parts.forEach(p => {
            if (/\.(jpeg|jpg|png|gif|webp|bmp)$/i.test(p) || /\/api\/upload\/download\//.test(p)) {
              imageUrls.add(p);
            }
          });
        });
        const existingUrls = new Set((leadAtt || []).map(a => a.url));
        const extras = Array.from(imageUrls)
          .filter(u => !existingUrls.has(u))
          .map(u => ({ id: u, url: u, originalName: u.split('/').pop(), isImage: true }));
        combined = [...(leadAtt || []), ...extras];
      } catch (e) {}
      setAttachments(combined);
    } catch (error) {
      console.error('获取跟进历史/附件失败:', error);
      setFollowUpHistory([]);
      setAttachments([]);
      // 如果获取线索详情失败，不要清空currentLead，保持列表中的数据
    }
  };

  const handleFollowUpSubmit = async (values) => {
    try {
      // 构建跟进记录内容，包含评级、标签和预约联系时间信息
      let followUpContent = values.content;
      
      // 添加评级信息
      if (values.rating_id) {
        const selectedRating = ratings.find(r => r.id === values.rating_id);
        if (selectedRating) {
          followUpContent += `\n\n【评级更新】${selectedRating.name}`;
        }
      }
      
      // 添加标签信息
      if (values.tags && values.tags.length > 0 && Array.isArray(tags)) {
        const selectedTags = tags.filter(t => values.tags.includes(t.id)).map(t => t.name);
        followUpContent += `\n\n【标签更新】${selectedTags.join('、')}`;
      }
      
      // 添加预约联系时间信息
      if (values.next_contact_at) {
        const contactTime = values.next_contact_at.format('YYYY-MM-DD HH:mm');
        followUpContent += `\n\n【预约联系时间】${contactTime}`;
      }
      
      console.log('准备添加跟进记录:', {
        leadId: currentLead.id,
        content: followUpContent,
        token: localStorage.getItem('token')
      });
      
      // 添加跟进记录，同时更新评级、标签、预约时间
      const followUpData = {
        content: followUpContent,
        rating_id: values.rating_id,
        tags: values.tags,
        next_contact_at: values.next_contact_at ? values.next_contact_at.format('YYYY-MM-DD HH:mm:ss') : null
      };
      
      const followUpResponse = await api.post(`/leads/${currentLead.id}/follow-ups`, followUpData);
      
      console.log('跟进记录添加成功:', followUpResponse.data);

      message.success('跟进记录添加成功');
      followUpForm.resetFields();
      
      // 刷新跟进历史和当前线索数据
      try {
        const response = await api.get(`/leads/${currentLead.id}`);
        const updatedLead = response.data;
        
        // 更新跟进历史
        setFollowUpHistory(updatedLead.follow_ups || []);
        
        // 更新当前线索数据
        setCurrentLead(updatedLead);
      } catch (error) {
        console.error('获取更新后的线索数据失败:', error);
        // 如果获取失败，至少刷新跟进历史
        try {
          const followUpResponse = await api.get(`/leads/${currentLead.id}/follow-ups`);
          setFollowUpHistory(followUpResponse.data || []);
        } catch (followUpError) {
          console.error('获取跟进历史失败:', followUpError);
        }
      }
      
      fetchLeads();
    } catch (error) {
      console.error('添加跟进记录失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error(`添加跟进记录失败: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleFilterChange = (key, value) => {
    console.log('筛选条件变化:', key, value);
    console.log('筛选器变化:', key, value);
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 前端筛选跟进状态
  const filterLeadsByFollowUpStatus = (leads, followUpStatus) => {
    if (!followUpStatus) return leads;
    
    const now = dayjs();
    const today = now.startOf('day');
    const tomorrow = today.add(1, 'day');
    
    console.log('筛选条件:', {
      followUpStatus,
      now: now.format('YYYY-MM-DD HH:mm:ss'),
      today: today.format('YYYY-MM-DD'),
      tomorrow: tomorrow.format('YYYY-MM-DD')
    });
    
    const filteredLeads = leads.filter(lead => {
      const nextContactAt = lead.next_contact_at ? dayjs(lead.next_contact_at) : null;
      
      let result = false;
      switch (followUpStatus) {
        case 'overdue':
          // 超时未联系：超过了预约的时间，没有联系的线索
          result = nextContactAt && nextContactAt.isBefore(now);
          break;
        case 'today':
          // 当天需联系：预约今天需要联系的线索
          result = nextContactAt && nextContactAt.isSame(today, 'day');
          break;
        case 'tomorrow':
          // 明天需联系：明天需要联系的线索
          result = nextContactAt && nextContactAt.isSame(tomorrow, 'day');
          break;
        default:
          result = true;
      }
      
      if (nextContactAt) {
        console.log(`线索 ${lead.name}: 预约时间 ${nextContactAt.format('YYYY-MM-DD HH:mm:ss')}, 筛选结果: ${result}`);
      }
      
      return result;
    });
    
    console.log('筛选结果:', filteredLeads.length, '条线索');
    return filteredLeads;
  };

  const handleClearAllFilters = () => {
    setFilters({
      search: '',
      gender: '',
      age_min: '',
      age_max: '',
      education_level: '',
      work_status: '',
      work_years_min: '',
      work_years_max: '',
      source_id: '',
      rating_id: '',
      status: '',
      tags: [],
      followUpStatus: ''
    });
  };

  const handleCreateInterview = (record) => {
    setCurrentLead(record);
    setSelectedDepartmentId(null);
    interviewForm.setFieldsValue({
      lead_id: record.id,
      lead_name: record.name,
      department_id: undefined,
      position_id: undefined,
      interviewer: user?.name || '',
      interview_date: dayjs().add(1, 'day'),
      notes: ''
    });
    setInterviewModalVisible(true);
  };



  const handleInterviewSubmit = async (values) => {
    try {
      // 获取选中的岗位和部门信息
      const positionResponse = await api.get(`/positions/${values.position_id}`);
      const position = positionResponse.data;
      
      const departmentResponse = await api.get(`/departments/${values.department_id}`);
      const department = departmentResponse.data;
      
      const data = {
        lead_id: values.lead_id,
        position: position.name, // 保存岗位名称
        position_id: values.position_id, // 保存岗位ID
        department_id: values.department_id, // 保存部门ID
        interviewer: values.interviewer,
        interview_date: values.interview_date.format('YYYY-MM-DD HH:mm:ss'),
        notes: values.notes
      };

      await api.post('/interviews', data);

      // 更新线索状态为待面试
      const updateData = {
        name: currentLead.name,
        phone: currentLead.phone,
        gender: currentLead.gender,
        wechat: currentLead.wechat,
        source_id: currentLead.source_id,
        rating_id: currentLead.rating_id,
        notes: currentLead.notes,
        status: 'pending' // 设置为待面试状态
      };
      
      // 只有当邮箱不为空且格式正确时才包含
      if (currentLead.email && currentLead.email.includes('@')) {
        updateData.email = currentLead.email;
      }
      
      await api.put(`/leads/${values.lead_id}`, updateData);

      message.success('面试创建成功');
      setInterviewModalVisible(false);
      interviewForm.resetFields();
      setSelectedDepartmentId(null);
      fetchLeads(); // 刷新线索列表
    } catch (error) {
      console.error('创建面试失败:', error);
      message.error(error.response?.data?.message || '创建面试失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, values);
        // 绑定本次手机上传会话（若存在）到当前线索
        try {
          const uploadId = window.__LAST_UPLOAD_ID__;
          if (uploadId) {
            await api.post('/upload/bind', { uploadId, leadId: editingLead.id });
            delete window.__LAST_UPLOAD_ID__;
          }
        } catch (e) { /* 忽略绑定失败 */ }
        message.success('更新成功');
        
        // 如果当前正在查看这个线索的详情，刷新当前线索信息
        if (currentLead && currentLead.id === editingLead.id) {
          try {
            const updatedLeadResponse = await api.get(`/leads/${editingLead.id}`);
            setCurrentLead(updatedLeadResponse.data);
            
            // 同时更新线索列表中的对应项
            setLeads(prevLeads => 
              prevLeads.map(lead => 
                lead.id === editingLead.id ? updatedLeadResponse.data : lead
              )
            );
          } catch (error) {
            console.error('刷新线索详情失败:', error);
            // 如果刷新失败，仍然刷新整个列表
            fetchLeads();
          }
        } else {
          // 如果不在查看详情，只刷新列表
          fetchLeads();
        }
      } else {
        await api.post('/leads', values);
        message.success('创建成功');
        fetchLeads();
      }
      setModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 丢回公海功能
  const handleDiscardToPublic = async (lead) => {
    try {
      await api.put(`/leads/${lead.id}/discard`);
      message.success('线索已丢回公海');
      setFollowUpDrawerVisible(false);
      fetchLeads();
    } catch (error) {
      message.error(error.response?.data?.message || '丢回公海失败');
    }
  };



  // 处理状态修改
  const handleStatusEdit = async (values) => {
    try {
      // 确保传递必要的字段，避免后端验证失败
      const updateData = {
        name: currentLead.name,
        phone: currentLead.phone,
        status: values.status
      };
      
      // 只有当邮箱不为空且格式正确时才包含
      if (currentLead.email && currentLead.email.includes('@')) {
        updateData.email = currentLead.email;
      }
      
      await api.put(`/leads/${currentLead.id}`, updateData);
      message.success('状态修改成功');
      setStatusEditVisible(false);
      // 更新当前线索状态
      setCurrentLead(prev => ({ ...prev, status: values.status }));
      // 刷新列表
      fetchLeads();
    } catch (error) {
      message.error(error.response?.data?.message || '状态修改失败');
    }
  };

  // 生成拨打链接
  // 存储每条线索的二维码链接，避免把Promise传给二维码组件
  const [qrLinkByLead, setQrLinkByLead] = useState({});

  const ensureQrLink = useCallback(async (lead) => {
    if (!lead?.id || !lead?.phone) return;
    if (qrLinkByLead[lead.id]) return; // 已生成
    try {
      // 使用登录态请求一次性code（后端从JWT中识别用户）
      const resp = await api.post('/auth/device-code');
      const code = resp.data?.code || '';
      const baseUrl = window.location.origin;
      const params = new URLSearchParams({ phone: lead.phone, lead_id: lead.id });
      if (code) params.append('code', code);
      if (lead.name) params.append('name', lead.name);
      if (lead.last_follow_up_at) {
        const t = new Date(lead.last_follow_up_at).toLocaleString('zh-CN', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
        });
        params.append('lastContact', t);
      }
      const link = `${baseUrl}/mobile/call?${params.toString()}`;
      setQrLinkByLead(prev => ({ ...prev, [lead.id]: link }));
    } catch (e) {
      message.error('生成扫码码失败');
    }
  }, [qrLinkByLead]);

  // 更新手机端页面信息
  const updateMobilePage = async (leadId, phoneNumber, leadName = '', lastContactTime = '') => {
    try {
      console.log('桌面端点击拨打，线索ID:', leadId);
      
      // 更新当前线索的updated_at时间戳，触发手机端轮询更新
      if (leadId) {
        console.log('调用时间戳更新API:', `/leads/${leadId}/timestamp`);
        const response = await api.put(`/leads/${leadId}/timestamp`);
        console.log('时间戳更新响应:', response.data);
        message.success('已更新手机端页面信息，请在手机上查看最新线索');
      } else {
        console.error('线索ID不存在');
        message.error('线索信息不完整，请重试');
      }
    } catch (error) {
      console.error('更新线索信息失败:', error);
      message.error('更新失败，请重试');
    }
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 100,
      render: (text, record) => (
        <div 
          style={{ fontWeight: 'bold', cursor: 'pointer', color: '#1890ff' }}
          onClick={() => handleFollowUp(record)}
          title="点击查看跟进历史"
        >
          {text}
        </div>
      )
    },
    {
      title: '联系方式',
      key: 'contact',
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <div style={{ color: '#666' }}>
          <div 
            style={{ cursor: 'pointer', color: '#000', whiteSpace: 'nowrap' }}
            onClick={() => {
              navigator.clipboard.writeText(record.phone);
              message.success('手机号已复制到剪贴板');
            }}
            title="点击复制手机号"
          >
            手机：{record.phone}
          </div>
          {record.wechat && (
            <div 
              style={{ cursor: 'pointer', color: '#000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              onClick={() => {
                navigator.clipboard.writeText(record.wechat);
                message.success('微信号已复制到剪贴板');
              }}
              title="点击复制微信号"
            >
              微信：{record.wechat}
            </div>
          )}
        </div>
      )
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (gender) => {
        const color = gender === 'male' ? 'blue' : gender === 'female' ? 'pink' : 'default';
        const text = gender === 'male' ? '男' : gender === 'female' ? '女' : '未知';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '年龄',
      dataIndex: 'age',
      key: 'age',
      width: 80,
      render: (age) => age ? `${age}岁` : '-'
    },
    {
      title: '评级',
      dataIndex: 'rating_name',
      key: 'rating_name',
      width: 100,
      render: (text, record) => 
        text ? <Tag color={record.rating_color}>{text}</Tag> : <Tag color="default">未评级</Tag>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusMap = {
          'active': { text: '在职中', color: 'green' },
          'inactive': { text: '已离职', color: 'red' },
          'pending': { text: '待面试', color: 'orange' },
          'non_employee': { text: '非员工', color: 'default' }
        };
        const statusInfo = statusMap[status] || { text: '非员工', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      }
    },

    {
      title: '来源',
      dataIndex: 'source_name',
      key: 'source_name',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '标签',
      key: 'tags',
      width: 120,
      render: (_, record) => {
        if (record.tags && Array.isArray(record.tags) && record.tags.length > 0) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {record.tags.map(tag => (
                <Tag key={tag.id} size="small" color="default">
                  {tag.name}
                </Tag>
              ))}
            </div>
          );
        }
        return '-';
      }
    },
    {
      title: '时间信息',
      key: 'time_info',
      width: 280,
      render: (_, record) => (
        <div style={{ lineHeight: '1.4', whiteSpace: 'nowrap' }}>
          <div>创建: {new Date(record.created_at).toLocaleString('zh-CN', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })}</div>
          <div>跟进: {record.last_follow_up_at ? new Date(record.last_follow_up_at).toLocaleString('zh-CN', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : '-'}</div>
          <div>预约: {record.next_contact_at ? new Date(record.next_contact_at).toLocaleString('zh-CN', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : '-'}</div>
        </div>
      )
    },
    {
      title: '归属销售',
      dataIndex: 'assigned_to_name',
      key: 'assigned_to_name',
      width: 120,
      render: (text, record) => {
        // 显示归属销售姓名，如果没有则显示"-"
        return text || '-';
      }
    },
    {
      title: '添加人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (text, record) => {
        // 如果是从agent导入的简历，显示"系统导入"
        if (record.source === '简历导入') {
          return <Tag color="blue">系统导入</Tag>;
        }
        // 显示创建人姓名，如果没有则显示"-"
        return text || '-';
      }
    },
    {
      title: '学历',
      dataIndex: 'education_level',
      key: 'education_level',
      width: 100,
      render: (level) => {
        if (!level) return '-';
        const colorMap = {
          '高中': 'default',
          '大专': 'blue',
          '本科': 'green',
          '硕士': 'orange',
          '博士': 'red'
        };
        return <Tag color={colorMap[level] || 'default'}>{level}</Tag>;
      }
    },
    {
      title: '工作状态',
      dataIndex: 'work_status',
      key: 'work_status',
      width: 120,
      render: (status) => {
        if (!status) return '-';
        const statusMap = {
          '在职': { text: '在职', color: 'green' },
          '离职': { text: '离职', color: 'red' },
          '待业': { text: '待业', color: 'orange' },
          '应届生': { text: '应届生', color: 'blue' }
        };
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      }
    },
    {
      title: '工作经验',
      dataIndex: 'work_years',
      key: 'work_years',
      width: 100,
      render: (years) => {
        if (!years) return '-';
        if (years === 0) return '应届生';
        return `${years}年`;
      }
    },
    {
      title: '当前职位',
      dataIndex: 'current_position',
      key: 'current_position',
      width: 180,
      render: (position) => renderTextWithTooltip(position, 3)
    },
    {
      title: '期望职位',
      dataIndex: 'expected_position',
      key: 'expected_position',
      width: 200,
      render: (position) => renderTextWithTooltip(position, 3)
    },
    {
      title: '期望薪资',
      dataIndex: 'expected_salary',
      key: 'expected_salary',
      width: 100,
      render: (salary) => salary || '-'
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<MessageOutlined />}
            onClick={() => handleFollowUp(record)}
            size="small"
            title="跟进记录"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            title="编辑"
          />
          <Button
            type="link"
            icon={<CalendarOutlined />}
            onClick={() => handleCreateInterview(record)}
            size="small"
            title="创建面试"
          />


        </Space>
      )
    }
  ];

  // 获取当前线索的面试和入职情况
  const fetchCurrentLeadStatus = useCallback(async (leadId) => {
    if (!leadId) return;
    
    try {
      // 获取面试记录
      const interviewsResponse = await api.get('/interviews');
      const allInterviews = Array.isArray(interviewsResponse.data) ? interviewsResponse.data : [];
      const leadInterviews = allInterviews.filter(interview => interview.lead_id === leadId);
      setCurrentLeadInterviews(leadInterviews);
      
      // 获取入职记录
      const onboardingsResponse = await api.get('/onboarding');
      const allOnboardings = Array.isArray(onboardingsResponse.data) ? onboardingsResponse.data : [];
      const leadOnboardings = allOnboardings.filter(onboarding => onboarding.lead_id === leadId);
      setCurrentLeadOnboardings(leadOnboardings);
    } catch (error) {
      console.error('获取线索状态信息失败:', error);
    }
  }, []);

  const handleOwnerChange = (e) => {
    setSelectedOwnerId(e.target.value);
  };

  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>
      {/* 查看模式与归属销售筛选 - 仅管理员可见 */}
      {hasPermission('lead.view_all') && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Text strong>查看模式：</Text>
              <Radio.Group 
                value={viewMode} 
                onChange={(e) => setViewMode(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="all">查看所有</Radio.Button>
                <Radio.Button value="my">查看我的</Radio.Button>
              </Radio.Group>
              <Text type="secondary">
                {viewMode === 'all' ? '显示所有员工领取过的简历' : '只显示分配给当前用户的简历'}
              </Text>
            </div>

            {/* 归属销售筛选，仅在查看所有时显示 */}
            {viewMode === 'all' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text strong>归属销售：</Text>
                <Radio.Group value={selectedOwnerId} onChange={handleOwnerChange}>
                  <Radio value="">
                    <Space>
                      全部
                      <Badge count={ownerOptions.reduce((sum, r) => sum + (r.lead_count || 0), 0)} showZero overflowCount={999999} />
                    </Space>
                  </Radio>
                  {ownerOptions.map(opt => (
                    <Radio key={opt.user_id} value={String(opt.user_id)}>
                      <Space>
                        {opt.display_name}
                        <Badge count={opt.lead_count || 0} showZero overflowCount={999999} />
                      </Space>
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* 筛选卡片 */}
      <Card 
        style={{ marginBottom: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>筛选条件</span>
            <Button 
              type="text" 
              onClick={handleClearAllFilters}
              size="small"
              style={{ padding: 0, fontSize: '12px' }}
            >
              清除筛选
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '30px', marginBottom: '16px' }}>
          <Form.Item label="性别" style={{ marginBottom: 0 }}>
              <Radio.Group
                value={filters.gender}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
                buttonStyle="solid"
              size="middle"
              >
                <Radio.Button value="">全部</Radio.Button>
                <Radio.Button value="male">男</Radio.Button>
                <Radio.Button value="female">女</Radio.Button>
                <Radio.Button value="unknown">未知</Radio.Button>
              </Radio.Group>
            </Form.Item>
          <Form.Item label="状态" style={{ marginBottom: 0 }}>
            <Radio.Group
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              buttonStyle="solid"
              size="middle"
            >
              <Radio.Button value="">全部</Radio.Button>
              <Radio.Button value="non_employee">非员工</Radio.Button>
              <Radio.Button value="pending">待面试</Radio.Button>
              <Radio.Button value="active">在职中</Radio.Button>
              <Radio.Button value="inactive">已离职</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '30px', marginBottom: '16px' }}>
          <Form.Item label="学历" style={{ marginBottom: 0 }}>
            <Radio.Group
              value={filters.education_level || ''}
              onChange={(e) => handleFilterChange('education_level', e.target.value)}
              buttonStyle="solid"
              size="middle"
            >
              <Radio.Button value="">全部</Radio.Button>
              <Radio.Button value="高中">高中</Radio.Button>
              <Radio.Button value="大专">大专</Radio.Button>
              <Radio.Button value="本科">本科</Radio.Button>
              <Radio.Button value="硕士">硕士</Radio.Button>
              <Radio.Button value="博士">博士</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="工作状态" style={{ marginBottom: 0 }}>
            <Radio.Group
              value={filters.work_status}
              onChange={(e) => handleFilterChange('work_status', e.target.value)}
              buttonStyle="solid"
              size="middle"
            >
              <Radio.Button value="">全部</Radio.Button>
              <Radio.Button value="在职">在职</Radio.Button>
              <Radio.Button value="离职">离职</Radio.Button>
              <Radio.Button value="待业">待业</Radio.Button>
              <Radio.Button value="应届生">应届生</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </div>
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <Form.Item label="来源" style={{ marginBottom: 0 }}>
              <Radio.Group
                value={filters.source_id}
                onChange={(e) => handleFilterChange('source_id', e.target.value)}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="">全部</Radio.Button>
                {sources && sources.map(source => (
                  <Radio.Button key={source.id} value={source.id}>
                    {source.name}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="评级" style={{ marginBottom: 0 }}>
              <Radio.Group
                value={filters.rating_id}
                onChange={(e) => handleFilterChange('rating_id', e.target.value)}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="">全部</Radio.Button>
                <Radio.Button value="unrated">未评级</Radio.Button>
                {ratings && ratings.map(rating => (
                  <Radio.Button key={rating.id} value={rating.id}>
                    {rating.name}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="预约" style={{ marginBottom: 0 }}>
              <Radio.Group
                value={filters.followUpStatus}
                onChange={(e) => handleFilterChange('followUpStatus', e.target.value)}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="">全部</Radio.Button>
                <Radio.Button value="overdue">
                  <Space>
                    <Badge status="error" />
                    <span style={{ color: '#f5222d' }}>超时未联系</span>
                  </Space>
                </Radio.Button>
                <Radio.Button value="today">
                  <Space>
                    <Badge status="warning" />
                    <span style={{ color: '#faad14' }}>当天需联系</span>
                  </Space>
                </Radio.Button>
                <Radio.Button value="tomorrow">
                  <Space>
                    <Badge status="success" />
                    <span style={{ color: '#52c41a' }}>明天需联系</span>
                  </Space>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ height: '16px' }}></div>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="搜索" style={{ marginBottom: 0 }}>
              <Input
                placeholder="姓名/手机号/微信"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
                style={{ width: '100%' }}
                size="middle"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Form.Item label="ID筛选" style={{ marginBottom: 0 }}>
              <Input
                placeholder="输入简历ID"
                value={filters.id}
                onChange={(e) => handleFilterChange('id', e.target.value)}
                allowClear
                style={{ width: '100%' }}
                size="middle"
                type="number"
                min="1"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="年龄范围" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input
                  placeholder="最小"
                  value={filters.age_min}
                  onChange={(e) => handleFilterChange('age_min', e.target.value)}
                  type="number"
                  min="16"
                  max="100"
                  style={{ width: '45%' }}
                  size="middle"
                />
                <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                <Input
                  placeholder="最大"
                  value={filters.age_max}
                  onChange={(e) => handleFilterChange('age_max', e.target.value)}
                  type="number"
                  min="16"
                  max="100"
                  style={{ width: '45%' }}
                  size="middle"
                />
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="工作年限" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input
                  placeholder="最小"
                  value={filters.work_years_min}
                  onChange={(e) => handleFilterChange('work_years_min', e.target.value)}
                  type="number"
                  min="0"
                  max="50"
                  style={{ width: '45%' }}
                  size="middle"
                />
                <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                <Input
                  placeholder="最大"
                  value={filters.work_years_max}
                  onChange={(e) => handleFilterChange('work_years_max', e.target.value)}
                  type="number"
                  min="0"
                  max="50"
                  style={{ width: '45%' }}
                  size="middle"
                />
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="标签" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder="选择标签筛选"
                value={filters.tags}
                onChange={(value) => handleFilterChange('tags', value)}
                allowClear
                style={{ width: '100%' }}
                size="middle"
              >
                {tags && tags.map(tag => (
                  <Option key={tag.id} value={tag.id}>
                    {tag.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 线索管理主卡片 */}
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>我的线索</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => fetchLeads(pagination.current)}
              loading={loading}
            >
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加线索
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      <Drawer
        title={editingLead ? '编辑线索' : '添加线索'}
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        width={520}
        placement="right"
        destroyOnClose
        closable
        zIndex={1000}
        className="add-lead-drawer"
      >
        <style>{`.add-lead-drawer .ant-col{flex:0 0 100% !important;max-width:100% !important;}`}</style>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="ownership"
                label="线索归属"
                initialValue="private"
              >
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="private">私有</Radio.Button>
                  <Radio.Button value="public">公海</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="gender"
                label="性别"
                initialValue="unknown"
              >
                <Select 
                  placeholder="请选择性别" 
                  allowClear
                  onDropdownVisibleChange={(open) => console.log('性别下拉框状态:', open)}
                  onSelect={(value) => console.log('选择性别:', value)}
                >
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                  <Option value="unknown">未知</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="phone"
                label="手机号"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
                ]}
              >
                <Input 
                  placeholder="请输入手机号" 
                  disabled={!!editingLead}
                  title={editingLead ? "手机号不可编辑" : ""}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="age"
                label="年龄"
              >
                <Input placeholder="请输入年龄" type="number" min="16" max="100" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="wechat"
                label="微信"
              >
                <Input placeholder="请输入微信号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="education_level"
                label="学历"
              >
                <Select 
                  placeholder="请选择学历" 
                  allowClear
                  onDropdownVisibleChange={(open) => console.log('学历下拉框状态:', open)}
                  onSelect={(value) => console.log('选择学历:', value)}
                >
                  <Option value="高中">高中</Option>
                  <Option value="大专">大专</Option>
                  <Option value="本科">本科</Option>
                  <Option value="硕士">硕士</Option>
                  <Option value="博士">博士</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="graduate_school"
                label="毕业院校"
              >
                <Input placeholder="请输入毕业院校" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="major"
                label="专业"
              >
                <Input placeholder="请输入专业" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="current_position"
                label="当前职位"
              >
                <Input placeholder="请输入当前职位" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="current_company"
                label="当前公司"
              >
                <Input placeholder="请输入当前公司" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="current_salary"
                label="当前薪资"
              >
                <Input placeholder="请输入当前薪资" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="expected_position"
                label="期望职位"
              >
                <Input placeholder="请输入期望职位" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expected_salary"
                label="期望薪资"
              >
                <Input placeholder="请输入期望薪资" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="source_id"
                label="线索来源"
                rules={[{ required: true, message: '请选择线索来源' }]}
              >
                <Select 
                  placeholder="请选择来源" 
                  onDropdownVisibleChange={(open) => console.log('线索来源下拉框状态:', open)}
                  onSelect={(value) => console.log('选择线索来源:', value)}
                >
                  {sources && sources.map(source => (
                    <Option key={source.id} value={source.id}>
                      {source.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="province"
                label="所在省份"
              >
                <Input placeholder="请输入所在省份" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="city"
                label="所在城市"
              >
                <Input placeholder="请输入所在城市" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="状态"
              >
                <Select placeholder="请选择状态" allowClear>
                  <Option value="non_employee">非员工</Option>
                  <Option value="pending">待面试</Option>
                  <Option value="active">在职中</Option>
                  <Option value="inactive">已离职</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>



          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="notes"
                label="备注"
              >
                <TextArea rows={4} placeholder="请输入备注信息" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="附件上传"
              >
                <AttachmentUpload leadId={editingLead?.id} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingLead ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* 查看详情/跟进记录抽屉 */}
      <Drawer
        title="线索详情与跟进"
        open={followUpDrawerVisible}
        onClose={() => setFollowUpDrawerVisible(false)}
        width="80%"
        placement="right"
        destroyOnClose
        closable={true}
        style={{ zIndex: 100 }}
        maskStyle={{ zIndex: 99 }}
      >
        {currentLead && (
          <>
            {/* 头部用户信息 */}
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '20px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  size={64} 
                  icon={<UserOutlined />} 
                  style={{ backgroundColor: '#1890ff', marginRight: '16px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Title level={3} style={{ margin: 0, marginRight: '8px' }}>
                        {currentLead.name}
                      </Title>
                      <Tag size="small" color={currentLead.rating_name ? 'blue' : 'default'} style={{ marginRight: '8px' }}>
                        {currentLead.rating_name || '未评级'}
                      </Tag>
                      <Tag 
                        size="small" 
                        color={getStatusColor(currentLead.status)} 
                        style={{ marginRight: '8px' }}
                        onClick={hasPermission('lead.edit') ? () => setStatusEditVisible(true) : undefined}
                        style={{ 
                          marginRight: '8px',
                          cursor: hasPermission('lead.edit') ? 'pointer' : 'default'
                        }}
                        title={hasPermission('lead.edit') ? '点击修改状态' : ''}
                      >
                        {getStatusText(currentLead.status)}
                      </Tag>
                      <Space size="small">
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingLead(currentLead);
                            form.setFieldsValue({
                              ...currentLead
                            });
                            setModalVisible(true);
                            setFollowUpDrawerVisible(false);
                          }}
                          size="small"
                          title="编辑线索"
                        />
                        <Button
                          type="link"
                          icon={<CalendarOutlined />}
                          onClick={() => handleCreateInterview(currentLead)}
                          size="small"
                          title="创建面试"
                        />
                      </Space>
                    </div>
                    <Popconfirm
                      title="确认丢回公海"
                      description="确定要将此线索丢回公海吗？此操作不可撤销。"
                      onConfirm={() => handleDiscardToPublic(currentLead)}
                      okText="确认"
                      cancelText="取消"
                      okType="danger"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        title="丢回公海"
                        style={{ opacity: 0.6, fontSize: '12px' }}
                      >
                        丢回公海
                      </Button>
                    </Popconfirm>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', color: '#666', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text 
                        style={{ cursor: 'pointer', color: '#000' }}
                        onClick={() => {
                          navigator.clipboard.writeText(currentLead.phone);
                          message.success('手机号已复制到剪贴板');
                        }}
                        title="点击复制手机号"
                      >
                        <PhoneOutlined /> {currentLead.phone}
                      </Text>
                      <Popover
                        content={
                          <div style={{ textAlign: 'center' }}>
                            {(() => {
                              // 预先生成链接，避免把Promise传入
                              ensureQrLink(currentLead);
                              const value = qrLinkByLead[currentLead.id] || '';
                              return (
                                <QRCode 
                                  key={`${currentLead.id}-${currentLead.last_follow_up_at}`}
                                  value={value}
                                  size={200}
                                  level="M"
                                />
                              );
                            })()}
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                              扫描二维码在手机上拨打<br/>
                              <span style={{ color: '#52c41a' }}>（自动登录）</span>
                            </div>
                          </div>
                        }
                        title="手机拨打"
                        trigger="hover"
                        placement="bottom"
                      >
                        <Button
                          type="text"
                          size="small"
                          style={{ color: '#52c41a', fontSize: '12px' }}
                          title="手机拨打"
                          onClick={() => {
                            updateMobilePage(
                              currentLead.id,
                              currentLead.phone,
                              currentLead.name,
                              currentLead.last_follow_up_at ? 
                                new Date(currentLead.last_follow_up_at).toLocaleString('zh-CN', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : ''
                            );
                          }}
                        >
                          拨打
                        </Button>
                      </Popover>
                    </div>
                    {currentLead.email && (
                      <Text 
                        style={{ cursor: 'pointer', color: '#000' }}
                        onClick={() => {
                          navigator.clipboard.writeText(currentLead.email);
                          message.success('邮箱已复制到剪贴板');
                        }}
                        title="点击复制邮箱"
                      >
                        <MailOutlined /> {currentLead.email}
                      </Text>
                    )}
                    {currentLead.wechat && (
                      <Text 
                        style={{ cursor: 'pointer', color: '#000' }}
                        onClick={() => {
                          navigator.clipboard.writeText(currentLead.wechat);
                          message.success('微信号已复制到剪贴板');
                        }}
                        title="点击复制微信号"
                      >
                        <GlobalOutlined /> {currentLead.wechat}
                      </Text>
                    )}
                  </div>
                  
                  {/* 时间信息：创建时间、领取时间、预约时间、联系次数 */}
                  <div style={{ display: 'flex', gap: '24px', color: '#666', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#666' }}>创建时间：</Text>
                      <Text style={{ fontSize: '14px' }}>
                        {currentLead.created_at ? new Date(currentLead.created_at).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </Text>
                </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#666' }}>领取时间：</Text>
                      <Text style={{ fontSize: '14px' }}>
                        {currentLead.assigned_at ? new Date(currentLead.assigned_at).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </Text>
              </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#666' }}>预约时间：</Text>
                      <Text style={{ fontSize: '14px' }}>
                        {currentLead.next_contact_at ? new Date(currentLead.next_contact_at).toLocaleString('zh-CN', {
                        year: 'numeric', 
                        month: '2-digit', 
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                        }) : '-'}
                    </Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#666' }}>联系次数：</Text>
                      <Text style={{ fontSize: '14px' }}>{currentLead.contact_count || 0}</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#666' }}>添加人：</Text>
                      <Text style={{ fontSize: '14px' }}>{currentLead.created_by_name || currentLead.created_by_username || '-'}</Text>
                    </div>
                  </div>
                  
                  {/* 使用Row和Col布局 */}
                  <Row gutter={16} style={{ marginBottom: '8px' }}>
                    {/* 左侧：标签和备注 (18列) */}
                    <Col span={18}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* 标签 */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Text strong style={{ fontSize: '14px', color: '#666', marginRight: '8px' }}>标签：</Text>
                          {currentLead.tags && currentLead.tags.length > 0 ? (
                            <Space size="small">
                              {currentLead.tags.map((tag, index) => (
                                <Tag key={index} size="small" color="blue">
                                  {typeof tag === 'object' ? tag.name : tag}
                                </Tag>
                              ))}
                            </Space>
                          ) : (
                            <Text style={{ fontSize: '14px' }}>-</Text>
                          )}
                        </div>
                        
                        {/* 备注 */}
                        <div>
                          <Text strong style={{ fontSize: '14px', color: '#666' }}>备注：</Text>
                          <Text style={{ fontSize: '14px' }}>{currentLead.notes || '-'}</Text>
                        </div>
                      </div>
                    </Col>
                    
                    {/* 右侧：下一条按钮和自动拨打电话 (6列) */}
                    <Col span={6}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                        <Button
                          type="primary"
                          icon={<RightOutlined />}
                          disabled={currentLeadIndex >= leads.length - 1}
                          onClick={handleNextLead}
                          size="small"
                          style={{ width: '115px' }}
                        >
                          下一条
                        </Button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Switch
                            size="small"
                            checked={autoCall}
                            onChange={setAutoCall}
                          />
                          <Text style={{ fontSize: '12px', color: '#666' }}>自动拨打电话</Text>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>

            {/* 三列布局 - 移到头部卡片外面 */}
            <Row gutter={24}>
              {/* 左侧：简历详细信息 */}
              <Col span={5}>
                <Card 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>简历详细信息</span>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                          setEditingLead(currentLead);
                          form.setFieldsValue({
                            ...currentLead
                          });
                          setModalVisible(true);
                        }}
                        title="编辑线索信息"
                      >
                        编辑
                      </Button>
                    </div>
                  }
                  size="small" 
                  style={{ height: 'fit-content' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <Text strong>年龄：</Text>
                      <Text>{currentLead.age ? `${currentLead.age}岁` : '-'}</Text>
                    </div>
                    <div>
                      <Text strong>性别：</Text>
                      <Text>{currentLead.gender === 'male' ? '男' : currentLead.gender === 'female' ? '女' : '未知'}</Text>
                    </div>
                    <div>
                      <Text strong>婚姻状况：</Text>
                      <Text>{currentLead.marital_status || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>学历：</Text>
                      <Text>{currentLead.education_level || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>毕业院校：</Text>
                      <Text>{currentLead.graduate_school || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>专业：</Text>
                      <Text>{currentLead.major || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>工作状态：</Text>
                      <Text>{currentLead.work_status || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>工作年限：</Text>
                      <Text>{currentLead.work_years ? `${currentLead.work_years}年` : '-'}</Text>
                    </div>
                    <div>
                      <Text strong>当前职位：</Text>
                      <Text>{currentLead.current_position || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>当前公司：</Text>
                      <Text>{currentLead.current_company || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>当前薪资：</Text>
                      <Text>{currentLead.current_salary || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>期望职位：</Text>
                      <Text>{currentLead.expected_position || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>期望薪资：</Text>
                      <Text>{currentLead.expected_salary || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>所在省份：</Text>
                      <Text>{currentLead.province || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>所在城市：</Text>
                      <Text>{currentLead.city || '-'}</Text>
                    </div>
                    <div>
                      <Text strong>居住地址：</Text>
                      <Text>{currentLead.address || '-'}</Text>
                    </div>

                    {/* 附件展示 */}
                    <Divider style={{ margin: '8px 0' }} />
                    <Title level={5} style={{ marginTop: 0 }}>附件</Title>
                    {attachments.length === 0 ? (
                      <Text type="secondary">暂无附件</Text>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                        {attachments.map((f) => {
                          const isImg = (f.mimetype && f.mimetype.startsWith('image/')) || f.isImage || (f.url && /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.url));
                          return (
                            <div key={f.id || f.url} style={{ textAlign: 'center' }}>
                              {isImg ? (
                                <img src={f.url} alt={f.originalName} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
                              ) : (
                                <div style={{ width: 96, height: 96, border: '1px solid #eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                                  <FileTextOutlined style={{ fontSize: 24, color: '#999' }} />
                                </div>
                              )}
                              <div style={{ marginTop: 4, fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.originalName}>{f.originalName}</div>
                              <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看/下载</a>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Card>
              </Col>

              {/* 中间：添加跟进记录 */}
              <Col span={10}>
                {/* 面试、入职情况概览 */}
                {currentLead && (
                  <Card 
                    size="small" 
                    style={{ marginBottom: 12, backgroundColor: '#f8f9fa' }}
                    bodyStyle={{ padding: '8px 12px' }}
                  >
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#666',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>
                        📋 <strong>面试情况</strong>: 
                        {Array.isArray(currentLeadInterviews) && currentLeadInterviews.length > 0 ? (
                          <span style={{ color: '#1890ff' }}>
                            {currentLeadInterviews.length}次面试
                            {currentLeadInterviews.some(i => i.status === 'suitable') && ' (含合适)'}
                            {currentLeadInterviews.some(i => i.status === 'eliminated') && ' (含淘汰)'}
                            {currentLeadInterviews.some(i => i.status === 'no_show') && ' (含爽约)'}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>暂无面试记录</span>
                        )}
                      </span>
                      <span>
                        🏢 <strong>入职情况</strong>: 
                        {Array.isArray(currentLeadOnboardings) && currentLeadOnboardings.length > 0 ? (
                          <span style={{ color: '#52c41a' }}>
                            {currentLeadOnboardings.length}次入职
                            {currentLeadOnboardings.some(o => o.status === 'completed') && ' (含已完成)'}
                            {currentLeadOnboardings.some(o => o.status === 'in_progress') && ' (含进行中)'}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>暂无入职记录</span>
                        )}
                      </span>
                    </div>
                  </Card>
                )}
                
                <Card title="添加跟进记录" size="small">
                  <Form
                    form={followUpForm}
                    layout="vertical"
                    onFinish={handleFollowUpSubmit}
                  >
                    {/* 上传区域：左侧紧凑拖拽，右侧扫码按钮 */}
                    <Row gutter={8} style={{ marginBottom: 12 }}>
                      <Col span={19}>
                        <AttachmentUpload
                          ref={uploadRef}
                          leadId={currentLead?.id}
                          compact
                          showQrButton={false}
                          autoCreateFollowUp
                          hideList
                          attachToLead={false}
                          onAfterAutoFollowUp={() => currentLead && handleFollowUp(currentLead)}
                        />
                      </Col>
                      <Col span={5} style={{ display: 'flex', alignItems: 'stretch', flexDirection: 'column' }}>
                        <Button type="primary" block onClick={() => uploadRef.current?.openQRCode()}>
                          手机上传
                        </Button>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 6, textAlign: 'center' }}>
                          手机直接上传文件
                        </div>
                      </Col>
                    </Row>
                    <Form.Item
                      name="content"
                      label="沟通记录"
                      rules={[{ required: true, message: '请输入沟通记录' }]}
                    >
                      <Input.TextArea 
                        rows={8} 
                        placeholder="请详细记录与候选人的沟通内容... 可粘贴/保留上传得到的下载链接，系统会在历史中自动展示为图片"
                      />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="rating_id"
                          label="更新评级"
                        >
                          <Select placeholder="选择评级" allowClear>
                            {ratings && ratings.map(rating => (
                              <Option key={rating.id} value={rating.id}>
                                {rating.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="next_contact_at"
                          label="预约联系时间"
                        >
                          <DatePicker 
                            showTime 
                            placeholder="选择预约联系时间"
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD HH:mm"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="tags"
                          label="更新标签"
                        >
                          <Select
                            mode="multiple"
                            placeholder="选择标签"
                            allowClear
                          >
                            {tags && tags.map(tag => (
                              <Option key={tag.id} value={tag.id}>
                                {tag.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item style={{ textAlign: 'right', marginBottom: 0, marginTop: 16 }}>
                      <Button type="primary" htmlType="submit">
                        保存跟进
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </Col>

              {/* 右侧：跟进历史 */}
              <Col span={9}>
                <Card 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>跟进历史</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>显示系统操作</Text>
                        <Switch 
                          size="small"
                          checked={showSystemOperations}
                          onChange={setShowSystemOperations}
                        />
                        <Button
                          type="text"
                          icon={<ReloadOutlined />}
                          size="small"
                          onClick={() => {
                            if (currentLead) {
                              handleFollowUp(currentLead);
                            }
                          }}
                          title="刷新跟进历史"
                        />
                      </div>
                    </div>
                  } 
                  size="small"
                >
                  <List
                    dataSource={followUpHistory.filter(item => {
                      const c = item.content || '';
                      if (c.includes('【系统操作】线索已认领')) return false;
                      return showSystemOperations || !c.includes('【系统操作】');
                    })}
                    renderItem={(item, index) => (
                      <List.Item style={{ padding: '12px 0' }}>
                        <div style={{ width: '100%' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            marginBottom: '8px'
                          }}>
                            <Text strong style={{ fontSize: '14px' }}>
                              跟进记录 #{followUpHistory.filter(it => {
                                const cc = it.content || '';
                                if (cc.includes('【系统操作】线索已认领')) return false;
                                return showSystemOperations || !cc.includes('【系统操作】');
                              }).length - index}
                            </Text>
                            <div style={{ textAlign: 'right', minWidth: '200px', flexShrink: 0 }}>
                              <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {item.created_by_name || item.created_by_username || '未知用户'} · {new Date(item.created_at).toLocaleString('zh-CN', { 
                                  year: 'numeric', 
                                  month: '2-digit', 
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Text>
                            </div>
                          </div>
                          <div style={{ 
                            background: item.content.includes('【系统操作】') ? '#fff2e8' : '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '6px',
                            fontSize: '14px',
                            lineHeight: '1.8',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            border: item.content.includes('【系统操作】') ? '1px solid #ffd591' : 'none',
                            minHeight: '60px',
                            maxHeight: 'none'
                          }}>
                            {renderFollowUpContent(item.content)}
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                </Card>
              </Col>
            </Row>


          </>
        )}
      </Drawer>

      {/* 面试创建模态框 */}
      <Modal
        title="创建面试"
        open={interviewModalVisible}
        onCancel={() => setInterviewModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={interviewForm}
          layout="vertical"
          onFinish={handleInterviewSubmit}
        >
          <Form.Item
            name="lead_id"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lead_name"
            label="候选人姓名"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <DepartmentSelect 
              placeholder="请选择部门" 
              onChange={(value) => {
                setSelectedDepartmentId(value);
                interviewForm.setFieldsValue({ position_id: undefined });
              }}
            />
          </Form.Item>

          <Form.Item
            name="position_id"
            label="面试职位"
            rules={[{ required: true, message: '请选择面试职位' }]}
          >
            <PositionSelect 
              placeholder="请选择面试职位" 
              departmentId={selectedDepartmentId}
              disabled={!selectedDepartmentId}
            />
          </Form.Item>

          <Form.Item
            name="interviewer"
            label="面试官"
            rules={[{ required: true, message: '请输入面试官姓名' }]}
          >
            <Input placeholder="请输入面试官姓名" />
          </Form.Item>

          <Form.Item
            name="interview_date"
            label="面试时间"
            rules={[{ required: true, message: '请选择面试时间' }]}
          >
            <DatePicker 
              showTime 
              placeholder="选择面试时间"
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="面试备注"
          >
            <TextArea rows={4} placeholder="请输入面试备注" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setInterviewModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建面试
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 状态编辑模态框 */}
      <Modal
        title="修改线索状态"
        open={statusEditVisible}
        onCancel={() => setStatusEditVisible(false)}
        footer={null}
        width={400}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusEdit}
          initialValues={{ status: currentLead?.status }}
        >
          <Form.Item
            name="status"
            label="线索状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="new">新线索</Option>
              <Option value="contacted">已联系</Option>
              <Option value="qualified">合格</Option>
              <Option value="unqualified">不合格</Option>
              <Option value="converted">已转化</Option>
              <Option value="non_employee">非员工</Option>
              <Option value="pending">待面试</Option>
              <Option value="interviewed">已面试</Option>
              <Option value="offered">已发offer</Option>
              <Option value="hired">已入职</Option>
              <Option value="rejected">已拒绝</Option>
              <Option value="onboarding">入职中</Option>
              <Option value="active">在职中</Option>
              <Option value="inactive">已离职</Option>
              <Option value="no_show">未到场</Option>
              <Option value="cancelled">已取消</Option>
              <Option value="resigning">离职中</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setStatusEditVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
    </main>
  );
};

export default MyLeads; 